/**
 * RefereePortal.js - Referee-facing portal accessed via magic link token.
 * Mobile-first, inline-styled. No auth required — token-based access.
 * Supports score submission when league.refereeScoreEntry is enabled.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getRefereeSchedule, submitRefereeScore } from '../services/api';
import { getSportConfig } from '../utils/sportConfig';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';

function RefereePortal() {
  const { token } = useParams();
  const { colors } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referee, setReferee] = useState(null);
  const [league, setLeague] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [scoringGameId, setScoringGameId] = useState(null);
  const [scoreInputs, setScoreInputs] = useState({ team1Score: '', team2Score: '' });
  const [submittingScore, setSubmittingScore] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Override body background
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = colors.bgSecondary;
    return () => { document.body.style.background = prev; };
  }, [colors.bgSecondary]);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await getRefereeSchedule(token);
        setReferee(data.referee);
        setLeague(data.league);
        setSchedule(data.schedule);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [token]);

  const sportConfig = league ? getSportConfig(league.sport) : getSportConfig();

  const handleSubmitScore = async (gameId) => {
    const t1 = parseInt(scoreInputs.team1Score, 10);
    const t2 = parseInt(scoreInputs.team2Score, 10);
    if (isNaN(t1) || isNaN(t2) || t1 < 0 || t2 < 0) {
      showToast('Please enter valid scores', 'error');
      return;
    }
    if (!sportConfig.allowTies && t1 === t2) {
      showToast(`Ties are not allowed in ${sportConfig.label}`, 'error');
      return;
    }
    setSubmittingScore(true);
    try {
      await submitRefereeScore(token, gameId, { team1Score: t1, team2Score: t2 });
      // Update local schedule to show pending status
      setSchedule(prev => prev.map(week => ({
        ...week,
        games: week.games.map(g =>
          g.id === gameId ? { ...g, pendingScore: { team1Score: t1, team2Score: t2 } } : g
        )
      })));
      setScoringGameId(null);
      setScoreInputs({ team1Score: '', team2Score: '' });
      showToast('Score submitted — waiting for admin approval');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmittingScore(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const canSubmitScore = (game) => {
    return league?.refereeScoreEntry
      && !game.played
      && !game.pendingScore
      && !game.isPlayoff
      && game.status !== 'cancelled'
      && game.status !== 'forfeit'
      && game.status !== 'postponed';
  };

  const styles = {
    container: {
      maxWidth: 600,
      margin: '0 auto',
      padding: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      textAlign: 'center',
      padding: '24px 16px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: 12,
      marginBottom: 16,
      color: '#fff'
    },
    refName: {
      fontSize: '1.4rem',
      fontWeight: 700,
      margin: '12px 0 4px',
      color: '#fff'
    },
    leagueInfo: {
      fontSize: '0.9rem',
      color: 'rgba(255,255,255,0.85)',
      margin: 0
    },
    card: {
      background: colors.card,
      borderRadius: 12,
      padding: '20px',
      marginBottom: 16,
      boxShadow: `0 1px 4px ${colors.shadow}`
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: colors.text,
      margin: '0 0 12px',
      borderBottom: `2px solid ${colors.brand}`,
      paddingBottom: 6
    },
    upcomingRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 0',
      borderBottom: `1px solid ${colors.border}`,
      minHeight: 44
    },
    upcomingDate: {
      minWidth: 80,
      flexShrink: 0
    },
    weekHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.85rem',
      fontWeight: 600,
      color: '#764ba2',
      padding: '6px 0',
      borderBottom: `1px solid ${colors.border}`,
      marginBottom: 6
    },
    gameRow: {
      padding: '8px 0',
      borderBottom: `1px solid ${colors.border}`
    },
    gameTime: {
      fontSize: '0.82rem',
      color: colors.textSecondary,
      minWidth: 70,
      flexShrink: 0
    },
    scoreBadge: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      padding: '2px 10px',
      borderRadius: 6,
      fontWeight: 700,
      fontSize: '0.82rem',
      flexShrink: 0
    },
    playoffBadge: {
      background: '#fff3e0',
      color: '#e67e22',
      padding: '2px 8px',
      borderRadius: 4,
      fontWeight: 600,
      fontSize: '0.72rem',
      flexShrink: 0
    },
    pendingBadge: {
      padding: '3px 8px',
      background: '#fff3e0',
      color: '#e67e22',
      borderRadius: 6,
      fontSize: '0.75rem',
      fontWeight: 600,
      flexShrink: 0
    },
    submitScoreBtn: {
      padding: '6px 14px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      fontSize: '0.78rem',
      fontWeight: 600,
      cursor: 'pointer',
      flexShrink: 0,
      minHeight: 44
    },
    scoreForm: {
      padding: '12px 14px 14px',
      background: colors.bgSecondary,
      borderBottom: `1px solid ${colors.border}`
    },
    scoreFormInputs: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: 8,
      marginBottom: 10
    },
    scoreFormGroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1,
      maxWidth: 120
    },
    scoreFormLabel: {
      fontSize: '0.75rem',
      color: colors.textSecondary,
      marginBottom: 3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      textAlign: 'center'
    },
    scoreFormInput: {
      width: '100%',
      padding: '10px',
      border: `1px solid ${colors.inputBorder}`,
      borderRadius: 6,
      fontSize: '1.1rem',
      textAlign: 'center',
      boxSizing: 'border-box',
      outline: 'none',
      background: colors.inputBg,
      color: colors.text,
      minHeight: 48
    },
    scoreFormDash: {
      fontSize: '1.2rem',
      color: colors.textSecondary,
      fontWeight: 600,
      paddingBottom: 10
    },
    scoreFormActions: {
      display: 'flex',
      gap: 8
    },
    scoreFormSubmit: {
      flex: 1,
      padding: '10px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      minHeight: 44
    },
    scoreFormCancel: {
      flex: 1,
      padding: '10px',
      background: colors.bgSecondary,
      color: colors.textSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: 6,
      fontSize: '0.9rem',
      fontWeight: 500,
      cursor: 'pointer',
      minHeight: 44
    },
    toast: {
      position: 'fixed',
      top: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      color: '#fff',
      padding: '10px 20px',
      borderRadius: 8,
      fontSize: '0.85rem',
      fontWeight: 500,
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      maxWidth: '90vw',
      textAlign: 'center'
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <Logo size="2rem" />
            <p style={{ color: colors.textSecondary, marginTop: 16 }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <Logo size="2rem" />
            <h2 style={{ color: colors.danger, margin: '16px 0 8px', fontSize: '1.2rem' }}>Invalid Link</h2>
            <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: 0 }}>
              This referee link may have expired or is invalid.
            </p>
            <p style={{ color: colors.textSecondary, fontSize: '0.82rem', marginTop: 12 }}>
              Contact your league organizer for a new link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Count games
  const totalGames = schedule.reduce((acc, w) => acc + w.games.length, 0);
  const playedGames = schedule.reduce((acc, w) => acc + w.games.filter(g => g.played).length, 0);
  const upcomingGames = [];
  schedule.forEach(week => {
    week.games.forEach(game => {
      if (!game.played && game.status === 'scheduled') {
        upcomingGames.push({ ...game, weekNum: week.week, weekDate: week.date });
      }
    });
  });

  return (
    <div style={styles.container}>
      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'error' ? colors.danger : '#27ae60'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <Logo size="1.5rem" />
        <h1 style={styles.refName}>{referee.name}</h1>
        <p style={styles.leagueInfo}>
          {league.name} &bull; {league.seasonYear}
        </p>
      </div>

      {/* Upcoming Games */}
      {upcomingGames.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Upcoming Games</h2>
          {upcomingGames.slice(0, 5).map((game) => (
            <div key={game.id} style={styles.upcomingRow}>
              <div style={styles.upcomingDate}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: colors.text }}>
                  {formatDate(game.date)}
                </div>
                <div style={{ fontSize: '0.78rem', color: colors.textSecondary }}>{game.time}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text }}>
                  {game.team1} <span style={{ color: colors.textSecondary, fontWeight: 400 }}>vs</span> {game.team2}
                </div>
                {game.venue && (
                  <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: 2 }}>
                    {game.venue.name}
                    {game.venue.address && ` — ${game.venue.address}`}
                  </div>
                )}
              </div>
              {game.pendingScore ? (
                <span style={styles.pendingBadge}>Pending</span>
              ) : canSubmitScore(game) ? (
                <button
                  onClick={() => {
                    setScoringGameId(game.id);
                    setScoreInputs({ team1Score: '', team2Score: '' });
                  }}
                  style={styles.submitScoreBtn}
                >
                  Submit
                </button>
              ) : game.isPlayoff ? (
                <span style={styles.playoffBadge}>Playoff</span>
              ) : null}
            </div>
          ))}
          {upcomingGames.length > 5 && (
            <p style={{ color: colors.textSecondary, fontSize: '0.82rem', textAlign: 'center', margin: '8px 0 0' }}>
              +{upcomingGames.length - 5} more game{upcomingGames.length - 5 !== 1 ? 's' : ''} below
            </p>
          )}
        </div>
      )}

      {/* Full Schedule */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Full Schedule</h2>
        {schedule.length === 0 ? (
          <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: 0 }}>No games assigned yet.</p>
        ) : (
          schedule.map((week) => (
            <div key={week.week} style={{ marginBottom: 16 }}>
              <div style={styles.weekHeader}>
                <span>Week {week.week}</span>
                <span style={{ fontWeight: 400, color: colors.textSecondary }}>{formatDate(week.date)}</span>
              </div>
              {week.games.map((game) => {
                const isScoring = scoringGameId === game.id;

                return (
                  <div key={game.id} style={styles.gameRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={styles.gameTime}>{game.time}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.9rem' }}>
                          <span style={{ fontWeight: 600, color: colors.text }}>{game.team1}</span>
                          <span style={{ color: colors.textSecondary, margin: '0 6px' }}>vs</span>
                          <span style={{ fontWeight: 600, color: colors.text }}>{game.team2}</span>
                        </div>
                        {game.venue && (
                          <div style={{ fontSize: '0.75rem', color: colors.textSecondary, marginTop: 2 }}>
                            {game.venue.name}
                            {game.venue.address && ` — ${game.venue.address}`}
                          </div>
                        )}
                      </div>
                      {game.status === 'forfeit' ? (
                        <span style={{ ...styles.playoffBadge, background: '#fef0e7', color: '#e67e22' }}>Forfeit</span>
                      ) : game.status === 'cancelled' ? (
                        <span style={{ ...styles.playoffBadge, background: '#fde8ea', color: '#dc3545' }}>Cancelled</span>
                      ) : game.status === 'postponed' ? (
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                          <span style={{ ...styles.playoffBadge, background: '#fff8e1', color: '#856404' }}>Postponed</span>
                          {game.rescheduledDate && (
                            <span style={{ fontSize: '0.72rem', color: colors.textSecondary, marginTop: 2 }}>
                              {formatDate(game.rescheduledDate)}{game.rescheduledTime ? ` ${game.rescheduledTime}` : ''}
                            </span>
                          )}
                        </span>
                      ) : game.played ? (
                        <span style={styles.scoreBadge}>
                          {game.team1Score} - {game.team2Score}
                        </span>
                      ) : game.pendingScore ? (
                        <span style={styles.pendingBadge}>Pending</span>
                      ) : canSubmitScore(game) ? (
                        <button
                          onClick={() => {
                            setScoringGameId(game.id);
                            setScoreInputs({ team1Score: '', team2Score: '' });
                          }}
                          style={styles.submitScoreBtn}
                        >
                          Submit
                        </button>
                      ) : game.isPlayoff ? (
                        <span style={styles.playoffBadge}>Playoff</span>
                      ) : null}
                    </div>

                    {/* Inline score form */}
                    {isScoring && (() => {
                      const t1 = parseInt(scoreInputs.team1Score, 10);
                      const t2 = parseInt(scoreInputs.team2Score, 10);
                      const bothFilled = !isNaN(t1) && !isNaN(t2);
                      const isTied = bothFilled && t1 === t2;
                      const tieBlocked = isTied && !sportConfig.allowTies;

                      // For winLossOnly sports, derive dropdown value from scoreInputs
                      const winLossValue = sportConfig.winLossOnly
                        ? (scoreInputs.team1Score === 1 && scoreInputs.team2Score === 0 ? 'team1'
                          : scoreInputs.team1Score === 0 && scoreInputs.team2Score === 1 ? 'team2'
                          : scoreInputs.team1Score === 0 && scoreInputs.team2Score === 0 && scoreInputs.team1Score !== '' ? 'draw'
                          : '')
                        : '';

                      const handleWinLossChange = (val) => {
                        if (val === 'team1') setScoreInputs({ team1Score: 1, team2Score: 0 });
                        else if (val === 'team2') setScoreInputs({ team1Score: 0, team2Score: 1 });
                        else if (val === 'draw') setScoreInputs({ team1Score: 0, team2Score: 0 });
                        else setScoreInputs({ team1Score: '', team2Score: '' });
                      };

                      return (
                        <div style={styles.scoreForm}>
                          {sportConfig.winLossOnly ? (
                            <div style={{ marginBottom: 10, textAlign: 'center' }}>
                              <select
                                value={winLossValue}
                                onChange={e => handleWinLossChange(e.target.value)}
                                style={{
                                  ...styles.scoreFormInput,
                                  width: '100%',
                                  maxWidth: 260,
                                  cursor: 'pointer'
                                }}
                              >
                                <option value="">Select result...</option>
                                <option value="team1">{game.team1} wins</option>
                                <option value="team2">{game.team2} wins</option>
                                {sportConfig.allowTies && <option value="draw">Draw</option>}
                              </select>
                            </div>
                          ) : (
                            <>
                              <div style={styles.scoreFormInputs}>
                                <div style={styles.scoreFormGroup}>
                                  <label style={styles.scoreFormLabel}>{game.team1}</label>
                                  <input
                                    type="number"
                                    min="0"
                                    inputMode="numeric"
                                    value={scoreInputs.team1Score}
                                    onChange={e => setScoreInputs(prev => ({ ...prev, team1Score: e.target.value }))}
                                    style={{ ...styles.scoreFormInput, ...(tieBlocked ? { borderColor: colors.danger } : {}) }}
                                    placeholder="0"
                                  />
                                </div>
                                <span style={styles.scoreFormDash}>-</span>
                                <div style={styles.scoreFormGroup}>
                                  <label style={styles.scoreFormLabel}>{game.team2}</label>
                                  <input
                                    type="number"
                                    min="0"
                                    inputMode="numeric"
                                    value={scoreInputs.team2Score}
                                    onChange={e => setScoreInputs(prev => ({ ...prev, team2Score: e.target.value }))}
                                    style={{ ...styles.scoreFormInput, ...(tieBlocked ? { borderColor: colors.danger } : {}) }}
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              {tieBlocked && (
                                <div style={{ color: colors.danger, fontSize: '0.8rem', textAlign: 'center', marginBottom: '0.5rem' }}>
                                  Ties are not allowed in {sportConfig.label}
                                </div>
                              )}
                            </>
                          )}
                          {league.bestOf && sportConfig.supportsBestOf && (
                            <div style={{ fontSize: '0.75rem', color: colors.textSecondary, textAlign: 'center', marginBottom: 8 }}>
                              Best of {league.bestOf} — winner needs {Math.ceil(league.bestOf / 2)}
                            </div>
                          )}
                          <div style={styles.scoreFormActions}>
                            <button
                              onClick={() => handleSubmitScore(game.id)}
                              disabled={submittingScore || tieBlocked || (sportConfig.winLossOnly && !winLossValue)}
                              style={{ ...styles.scoreFormSubmit, ...((tieBlocked || (sportConfig.winLossOnly && !winLossValue)) ? { opacity: 0.5 } : {}) }}
                            >
                              {submittingScore ? 'Submitting...' : 'Submit Score'}
                            </button>
                            <button
                              onClick={() => setScoringGameId(null)}
                              style={styles.scoreFormCancel}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Stats summary */}
      <div style={{ ...styles.card, textAlign: 'center', padding: '16px' }}>
        <div style={{ fontSize: '0.85rem', color: colors.text, fontWeight: 600, marginBottom: 4 }}>
          {playedGames} of {totalGames} games scored
        </div>
        <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
          {upcomingGames.length} upcoming
        </div>
      </div>
    </div>
  );
}

export default RefereePortal;
